// SR detail page — status transitions + assign carer.

import { type JSX } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shell } from "../components/Shell.js";
import { formatSrRef } from "../lib/refs.js";
import { AssignCarerSection, type CarerItem } from "../components/AssignCarerSection.js";
import { AssignAgentSection } from "../components/AssignAgentSection.js";
import { useSrMutations, usesr, useCarers } from "../hooks/useSrDetail.js";
import {
  RequestDetailsCard,
  SrInfoCard,
  PrescriptionsCard,
  CheckinSection,
  TransitionButtons,
  CsatSection,
  FamilySatisfactionSection,
  CSAT_STATUSES,
  FAM_SAT_STATUSES,
  isNsofarConsultSr,
  type SrDetail,
} from "./SrDetailPage.components.js";
import { ConsultPanel } from "../components/consult/ConsultPanel.js";
import { FamilyFeedbackSection, IncidentSafetySection } from "./SrDetailPage.feedback.js";

interface CarerListResponse { data: CarerItem[]; total: number; page: number; limit: number; }

export type { SrDetail, CarerListResponse };

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
export function SrDetailPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: sr, isLoading, isError } = usesr(id);
  const { data: carerData } = useCarers();
  const carers = carerData?.data ?? [];

  const { transition, assign, checkin, checkout, csat, famSat, patchSr } = useSrMutations(id, qc);

  if (isLoading) return <Shell><p className="p-8 text-sm text-slate-400" aria-busy="true">Loading…</p></Shell>;
  if (isError || !sr) return <Shell><p className="p-8 text-sm text-red-600">SR not found.</p></Shell>;

  const handlePatch = (data: Record<string, unknown>): void => {
    patchSr.mutate(data, { onSuccess: () => toast.success("Saved.") });
  };

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/sr", { state: { highlightId: sr._id } })} className="text-sm text-navy-700 hover:underline">← Back</button>
          <h1 className="text-xl font-semibold text-slate-900">{formatSrRef(sr)}</h1>
        </div>
        <SrInfoCard sr={sr} />
        {isNsofarConsultSr(sr) && (
          <ConsultPanel sr={sr} onChanged={() => void qc.invalidateQueries({ queryKey: ["sr", id] })} />
        )}
        <RequestDetailsCard fields={sr.nsofar_sr_fields} />
        <PrescriptionsCard srId={sr._id} />
        {sr.status === "in_progress" && !isNsofarConsultSr(sr) && <CheckinSection checkin={checkin} checkout={checkout} />}
        {/* nsofar consult SRs: lifecycle is nsofar-owned (docs/33 §7.7) — manual
            transitions 409 server-side, so the transition card is hidden and
            the ConsultPanel above is the only driver. */}
        {!isNsofarConsultSr(sr) && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-700">Status Transition</h2>
            <TransitionButtons sr={sr} onTransition={(s) => transition.mutate(s)} />
            {transition.isError && <p className="text-sm text-red-600">Transition failed.</p>}
          </div>
        )}
        {sr.category === "care_visit" ? (
          <AssignCarerSection currentCarerId={sr.assigned_carer_id} carers={carers}
            onAssign={(cid) => assign.mutate(cid)} />
        ) : (
          <AssignAgentSection
            serviceType={sr.service_type}
            srId={sr._id}
            currentAgentId={sr.assigned_agent_id}
            currentAgentType={sr.assigned_agent_type}
            onAssigned={() => void qc.invalidateQueries({ queryKey: ["sr", id] })}
          />
        )}
        {assign.isError && <p className="text-sm text-red-600">Assignment failed.</p>}
        {CSAT_STATUSES.has(sr.status) && <CsatSection existing={sr.csat_score} csat={csat} />}
        {FAM_SAT_STATUSES.has(sr.status) && (
          <FamilySatisfactionSection
            existing={sr.family_satisfaction_rating ?? null}
            notes={sr.family_feedback_notes ?? null}
            onSave={(n) => famSat.mutate(n)}
          />
        )}
        {FAM_SAT_STATUSES.has(sr.status) && (
          <FamilyFeedbackSection
            carerRequestedAgain={sr.carer_requested_again ?? false}
            carerExcludedByFamily={sr.carer_excluded_by_family ?? false}
            carerName={sr.assigned_carer_id ? `C-${sr.assigned_carer_id.slice(-5).toLowerCase()}` : null}
            patientRef={`P-${sr.patient_id.slice(-5).toLowerCase()}`}
            isPending={patchSr.isPending}
            onPatch={handlePatch}
          />
        )}
        <IncidentSafetySection
          incidentDuringAssignment={sr.incident_during_assignment ?? false}
          isPending={patchSr.isPending}
          onPatch={handlePatch}
        />
      </div>
    </Shell>
  );
}
